/**
 * Transformer management utilities
 * Extracted from Canvas.jsx to reduce file size
 */

/**
 * Applies image-specific node properties (dimensions, scale, offset)
 */
export const applyImageNodeProperties = (node, selectedObject) => {
  if (
    selectedObject.type === "Image" &&
    selectedObject.width &&
    selectedObject.height
  ) {
    node.width(selectedObject.width);
    node.height(selectedObject.height);

    const isApiImage =
      selectedObject.src &&
      (selectedObject.src.startsWith("http://") ||
        selectedObject.src.startsWith("https://"));
    const shouldApplyFlipViaScale =
      !selectedObject.hasBakedTransforms || isApiImage;

    const calculatedScaleX =
      selectedObject.flipHorizontal && shouldApplyFlipViaScale
        ? -Math.abs(selectedObject.scaleX || 1)
        : Math.abs(selectedObject.scaleX || 1);
    const calculatedScaleY =
      selectedObject.flipVertical && shouldApplyFlipViaScale
        ? -Math.abs(selectedObject.scaleY || 1)
        : Math.abs(selectedObject.scaleY || 1);

    node.scaleX(calculatedScaleX);
    node.scaleY(calculatedScaleY);

    // CRITICAL FIX: Respect useTopLeftAnchor - set offsets to 0 for top-left anchor, width/2 for center
    const useTopLeftAnchor = selectedObject.useTopLeftAnchor === true;
    const newOffsetX = useTopLeftAnchor ? 0 : selectedObject.width / 2;
    const newOffsetY = useTopLeftAnchor ? 0 : selectedObject.height / 2;

    node.offsetX(newOffsetX);
    node.offsetY(newOffsetY);
  }
};

/**
 * Configures transformer anchors based on element type and maintainAspectRatio
 */
export const configureTransformerAnchors = (
  targetTransformer,
  selectedObject
) => {
  if (
    selectedObject.type === "Rect" ||
    selectedObject.type === "Circle" ||
    selectedObject.type === "Image" ||
    selectedObject.type === "Text"
  ) {
    let anchors = [
      "top-left",
      "top-center",
      "top-right",
      "middle-left",
      "middle-right",
      "bottom-left",
      "bottom-center",
      "bottom-right",
    ];

    if (selectedObject?.maintainAspectRatio === true) {
      anchors = ["bottom-right"];
    } else if (selectedObject.type === "Text") {
      // CRITICAL FIX: For text elements, only show horizontal resize handles (middle-left and middle-right)
      // Text elements should only be resizable horizontally, not vertically
      // Height is automatically calculated based on content
      anchors = ["middle-left", "middle-right"];
    } else {
      anchors = ["middle-right", "bottom-center", "bottom-right"];
    }

    targetTransformer.enabledAnchors(anchors);
    targetTransformer.keepRatio(selectedObject?.maintainAspectRatio || false);

    if (
      selectedObject.type === "Circle" &&
      !selectedObject?.maintainAspectRatio
    ) {
      targetTransformer.shiftBehavior("invert");
    } else {
      targetTransformer.shiftBehavior("default");
    }
  }
};

/**
 * Finds which canvas an element belongs to based on state
 */
export const findElementCanvas = ({
  selectedObject,
  rectangles,
  circles,
  images,
  texts,
  arrows,
  lines,
  doubleSidedArrows,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasImages,
  offCanvasTexts,
  offCanvasArrows,
  offCanvasLines,
  offCanvasDoubleSidedArrows,
}) => {
  // CRITICAL: Check arrays first, but if element is not found, check if it's a unified element
  // that might not be in arrays yet (e.g., during selection before array update)
  const isInMainCanvas =
    (selectedObject.type === "Rect" &&
      rectangles.some((r) => r.id === selectedObject.id)) ||
    (selectedObject.type === "Circle" &&
      circles.some((c) => c.id === selectedObject.id)) ||
    (selectedObject.type === "Image" &&
      images.some((i) => i.id === selectedObject.id)) ||
    (selectedObject.type === "Text" &&
      texts.some((t) => t.id === selectedObject.id)) ||
    (selectedObject.type === "Arrow" &&
      (arrows.some((a) => a.id === selectedObject.id) ||
        // Also check lines array for unified arrow elements
        lines.some((l) => {
          if (l.id !== selectedObject.id) return false;
          const hasStartArrow =
            l.hasStartArrow === true || l.pointerAtBeginning === true;
          const hasEndArrow =
            l.hasEndArrow === true || l.pointerAtEnd === true;
          return hasStartArrow || hasEndArrow; // Unified arrow element
        }) ||
        // CRITICAL: If not in arrays, check if selectedObject itself has arrow properties
        // This handles cases where element is selected but not yet in arrays
        (selectedObject.hasStartArrow === true || selectedObject.hasEndArrow === true ||
          selectedObject.pointerAtBeginning === true || selectedObject.pointerAtEnd === true))) ||
    (selectedObject.type === "Line" &&
      (lines.some((l) => {
        if (l.id !== selectedObject.id) return false;
        // Return true for any line element, including unified arrows/lines
        // Unified elements can have arrow flags set, but they're still in the lines array
        return true;
      }) ||
        // CRITICAL: If not in arrays, accept any element with type "Line"
        // This includes unified elements that might have arrow flags
        true)) ||
    (selectedObject.type === "DoubleSidedArrow" &&
      (doubleSidedArrows.some((d) => d.id === selectedObject.id) ||
        // Also check lines array for unified double-sided arrow elements
        lines.some((l) => {
          if (l.id !== selectedObject.id) return false;
          const hasStartArrow =
            l.hasStartArrow === true || l.pointerAtBeginning === true;
          const hasEndArrow =
            l.hasEndArrow === true || l.pointerAtEnd === true;
          return hasStartArrow && hasEndArrow; // Unified double-sided arrow element
        }) ||
        // CRITICAL: If not in arrays, check if selectedObject has both arrow flags
        (selectedObject.hasStartArrow === true && selectedObject.hasEndArrow === true)));

  const isInOffCanvas =
    (selectedObject.type === "Rect" &&
      offCanvasRectangles.some((r) => r.id === selectedObject.id)) ||
    (selectedObject.type === "Circle" &&
      offCanvasCircles.some((c) => c.id === selectedObject.id)) ||
    (selectedObject.type === "Image" &&
      offCanvasImages.some((i) => i.id === selectedObject.id)) ||
    (selectedObject.type === "Text" &&
      offCanvasTexts.some((t) => t.id === selectedObject.id)) ||
    (selectedObject.type === "Arrow" &&
      (offCanvasArrows.some((a) => a.id === selectedObject.id) ||
        // Also check offCanvasLines array for unified arrow elements
        offCanvasLines.some((l) => {
          if (l.id !== selectedObject.id) return false;
          const hasStartArrow =
            l.hasStartArrow === true || l.pointerAtBeginning === true;
          const hasEndArrow =
            l.hasEndArrow === true || l.pointerAtEnd === true;
          return hasStartArrow || hasEndArrow; // Unified arrow element
        }))) ||
    (selectedObject.type === "Line" &&
      (offCanvasLines.some((l) => {
        if (l.id !== selectedObject.id) return false;
        // Return true for any line element, including unified arrows/lines
        // Unified elements can have arrow flags set, but they're still in the offCanvasLines array
        return true;
      }) ||
        // CRITICAL: If not in arrays, accept any element with type "Line"
        // This includes unified elements that might have arrow flags
        false)) ||
    (selectedObject.type === "DoubleSidedArrow" &&
      (offCanvasDoubleSidedArrows.some((d) => d.id === selectedObject.id) ||
        // Also check offCanvasLines array for unified double-sided arrow elements
        offCanvasLines.some((l) => {
          if (l.id !== selectedObject.id) return false;
          const hasStartArrow =
            l.hasStartArrow === true || l.pointerAtBeginning === true;
          const hasEndArrow =
            l.hasEndArrow === true || l.pointerAtEnd === true;
          return hasStartArrow && hasEndArrow; // Unified double-sided arrow element
        })));

  return { isInMainCanvas, isInOffCanvas };
};

/**
 * Attaches transformer to a node with proper configuration
 */
export const attachTransformerToNode = ({
  selectedObject,
  stageRef,
  offCanvasStageRef,
  topLayerStageRef,
  transformerRef,
  offCanvasTransformerRef,
  topLayerTransformerRef,
  rectangles,
  circles,
  images,
  texts,
  arrows,
  lines,
  doubleSidedArrows,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasImages,
  offCanvasTexts,
  offCanvasArrows,
  offCanvasLines,
  offCanvasDoubleSidedArrows,
}) => {
  let node = null;
  let sourceStage = null;
  let targetTransformer = null;

  const { isInMainCanvas, isInOffCanvas } = findElementCanvas({
    selectedObject,
    rectangles,
    circles,
    images,
    texts,
    arrows,
    lines,
    doubleSidedArrows,
    offCanvasRectangles,
    offCanvasCircles,
    offCanvasImages,
    offCanvasTexts,
    offCanvasArrows,
    offCanvasLines,
    offCanvasDoubleSidedArrows,
  });

  if (isInMainCanvas && stageRef?.current) {
    node = stageRef.current.findOne(`#${selectedObject.id}`);
    if (node && node.getLayer()) {
      sourceStage = stageRef.current;
      targetTransformer = transformerRef.current;
    }
  }

  if (
    (!node || selectedObject?.isOnTopLayer) &&
    topLayerStageRef?.current &&
    selectedObject?.type === "Image" &&
    selectedObject?.isOnTopLayer
  ) {
    node = topLayerStageRef.current.findOne(`#${selectedObject.id}`);
    if (node && node.getLayer()) {
      sourceStage = topLayerStageRef.current;
      targetTransformer =
        topLayerTransformerRef?.current || transformerRef.current;
    }
  }

  if ((!node || isInOffCanvas) && offCanvasStageRef?.current) {
    node = offCanvasStageRef.current.findOne(`#${selectedObject.id}`);
    if (node && node.getLayer()) {
      sourceStage = offCanvasStageRef.current;
      targetTransformer = offCanvasTransformerRef.current;
    }
  }

  return { node, sourceStage, targetTransformer };
};

/**
 * Validates that a node still exists and is valid
 */
export const validateNode = (
  node,
  transformerRef,
  offCanvasTransformerRef,
  topLayerTransformerRef
) => {
  try {
    const layer = node.getLayer();
    if (!layer || !layer.children || !layer.children.includes(node)) {
      clearTransformers(
        transformerRef,
        offCanvasTransformerRef,
        topLayerTransformerRef
      );
      return false;
    }
    return true;
  } catch (e) {
    clearTransformers(
      transformerRef,
      offCanvasTransformerRef,
      topLayerTransformerRef
    );
    return false;
  }
};

/**
 * Clears all transformers
 */
export const clearTransformers = (...transformerRefs) => {
  transformerRefs.forEach((ref) => {
    if (ref?.current) {
      ref.current.nodes([]);
      if (ref.current.getLayer()) {
        ref.current.getLayer().batchDraw();
      }
    }
  });
};

/** Pointer position from a Konva transform/drag event (mouse or touch). */
export const getClientCoordsFromKonvaPointerEvent = (evt) => {
  if (!evt) return { clientX: undefined, clientY: undefined };
  if (evt.clientX != null && evt.clientY != null) {
    return { clientX: evt.clientX, clientY: evt.clientY };
  }
  const t = evt.touches?.[0] || evt.changedTouches?.[0];
  if (t) return { clientX: t.clientX, clientY: t.clientY };
  return { clientX: undefined, clientY: undefined };
};

/** True if the viewport point lies inside the main canvas Konva stage container. */
export const isClientPointInsideMainCanvasStage = (clientX, clientY, mainStageRef) => {
  if (
    clientX == null ||
    clientY == null ||
    !mainStageRef?.current?.container
  ) {
    return false;
  }
  const rect = mainStageRef.current.container().getBoundingClientRect();
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
};

/**
 * Snapshot a node's transform at transform start so we can revert if the pointer
 * moves over the main canvas while resizing off-canvas elements.
 */
export const captureKonvaNodeTransformSnapshot = (node, elementType, elementId) => {
  if (!node) return null;
  const base = {
    id: elementId,
    type: elementType,
    x: node.x(),
    y: node.y(),
    rotation: node.rotation(),
    scaleX: node.scaleX(),
    scaleY: node.scaleY(),
  };
  if (elementType === "Circle") {
    const r =
      typeof node.radius === "function" ? node.radius() : node.width() / 2;
    return {
      ...base,
      radius: r,
      width: node.width(),
      height: node.height(),
    };
  }
  return {
    ...base,
    width: node.width(),
    height: node.height(),
  };
};

export const revertKonvaNodeToTransformSnapshot = (node, snap) => {
  if (!node || !snap) return;
  node.x(snap.x);
  node.y(snap.y);
  node.rotation(snap.rotation);
  node.scaleX(snap.scaleX);
  node.scaleY(snap.scaleY);
  if (snap.type === "Circle" && snap.radius != null) {
    node.radius(snap.radius);
  } else {
    node.width(snap.width);
    node.height(snap.height);
  }
  node.getLayer()?.batchDraw();
};

/** Build React state attrs from a node after revert (normalized scale). */
export const buildOffCanvasAttrsFromNode = (node, elementType, elementId) => {
  const x = node.x();
  const y = node.y();
  const rot = node.rotation();
  const sx = node.scaleX();
  const sy = node.scaleY();
  const newW = node.width() * Math.abs(sx || 1);
  const newH = node.height() * Math.abs(sy || 1);
  if (elementType === "Circle") {
    const radius = Math.min(newW, newH) / 2;
    return {
      id: elementId,
      x,
      y,
      width: newW,
      height: newH,
      radius,
      rotation: rot,
      scaleX: 1,
      scaleY: 1,
    };
  }
  return {
    id: elementId,
    x,
    y,
    width: newW,
    height: newH,
    rotation: rot,
    scaleX: 1,
    scaleY: 1,
  };
};
